import { httpService, HttpError } from '@/utils/httpService';
import type {
  LoginRequest,
  BearerResponse,
  UserCreate,
  UserRead,
  UserUpdate,
} from '@/types/api';

async function login(credentials: LoginRequest): Promise<BearerResponse> {
  try {
    const response = await httpService.postForm<any>('/auth/jwt/login', {
      username: credentials.username,
      password: credentials.password,
    });

    // Cookie is automatically stored by browser
    return response;
  } catch (error) {
    if (error instanceof HttpError && error.status === 400) {
      throw new HttpError(
        'Invalid credentials or user not verified',
        error.status
      );
    }
    console.log('Login error:', error);
    throw error;
  }
}

async function logout(): Promise<void> {
  try {
    await httpService.post('/auth/jwt/logout');
    // Cookie is automatically cleared by backend
    // Force reload to clear any cached auth state
  } catch (error) {
    console.warn('Logout API call failed:', error);
    // Still redirect even if logout fails
  }
}

async function register(userData: UserCreate): Promise<UserRead> {
  try {
    return await httpService.post<UserRead>('/auth/register', userData);
  } catch (error) {
    if (error instanceof HttpError && error.status === 400) {
      const detail = error.details?.detail;

      if (typeof detail === 'string') {
        if (detail === 'REGISTER_USER_ALREADY_EXISTS') {
          throw new HttpError(
            'User with this email already exists',
            error.status
          );
        }
      } else if (detail && typeof detail === 'object') {
        const code =
          'code' in detail && typeof detail.code === 'string'
            ? detail.code
            : undefined;
        const reason =
          'reason' in detail && typeof detail.reason === 'string'
            ? detail.reason
            : undefined;

        if (code === 'REGISTER_INVALID_PASSWORD') {
          throw new HttpError(
            `Password validation failed: ${reason ?? 'Invalid password'}`,
            error.status
          );
        }
      }
    }
    throw error;
  }
}

async function getCurrentUser(): Promise<UserRead> {
  return httpService.get<UserRead>('/auth/users/me');
}

async function updateCurrentUser(userData: UserUpdate): Promise<UserRead> {
  return httpService.patch<UserRead>('/auth/users/me', userData);
}

export const authService = {
  login,
  logout,
  register,
  getCurrentUser,
  updateCurrentUser,
} as const;
